/**
 * tasksChanged — one signal for "a task was created, edited, resolved,
 * reopened or deleted", so every list of tasks (My Tasks, All Project Tasks,
 * the Overview's Your Tasks card, the rail's past-due badge) refetches
 * instead of waiting for its next poll.
 *
 * It is raised in ONE place: apiFetch, after any successful write to a task
 * route (`/punches` or `/api/me/tasks`). Nothing else has to remember to
 * call it, whichever dialog or panel made the change.
 *
 *   - same tab: a window event, TASKS_CHANGED_EVENT;
 *   - other tabs: a BroadcastChannel ping, re-raised there as the same event.
 *
 * Listeners subscribe with onTasksChanged(cb); bursts are debounced so a
 * multi-step change (create, then assign) refetches once.
 */
export const TASKS_CHANGED_EVENT = 'pidly:tasks-changed';

const TASK_WRITE_RE = /\/punches(\/|\?|$)|\/api\/me\/tasks(\/|\?|$)/;
const WRITES = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let channel = null;
function getChannel() {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try {
    channel = new BroadcastChannel('pidly-tasks');
    channel.onmessage = () => {
      window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT, { detail: { remote: true } }));
    };
  } catch { channel = null; }
  return channel;
}
// Open the channel at load so this tab hears other tabs from the start.
if (typeof window !== 'undefined') getChannel();

/** True for a write to a task route. */
export function isTaskWrite(url, method) {
  return WRITES.has(String(method || 'GET').toUpperCase()) && TASK_WRITE_RE.test(String(url || ''));
}

/** Tell this tab and the others that tasks changed. */
export function notifyTasksChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT, { detail: { remote: false } }));
  try { getChannel()?.postMessage({ at: Date.now() }); } catch { /* closed */ }
}

/** Run cb (debounced) whenever tasks change here or in another tab. Returns the unsubscribe. */
export function onTasksChanged(cb, wait = 400) {
  if (typeof window === 'undefined') return () => {};
  let timer = null;
  const handler = () => {
    clearTimeout(timer);
    timer = setTimeout(() => cb(), wait);
  };
  window.addEventListener(TASKS_CHANGED_EVENT, handler);
  return () => { clearTimeout(timer); window.removeEventListener(TASKS_CHANGED_EVENT, handler); };
}
